import { Tooltip, TooltipTrigger } from "@artis/ui";
import cn from "mcn";
import { type ReactNode, useEffect, useRef, useState } from "react";

const isOverflowing = (el: HTMLElement) => el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;

/** Put `data-truncate-check` on the overflowing node. */
export const TruncatedTooltip = ({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const target = () => (root.querySelector("[data-truncate-check]") as HTMLElement | null) ?? root;
    const checkTruncation = () => setIsTruncated(isOverflowing(target()));

    checkTruncation();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(checkTruncation);
    observer?.observe(root);
    const marked = root.querySelector("[data-truncate-check]");
    if (marked && marked !== root) observer?.observe(marked);
    window.addEventListener("resize", checkTruncation);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", checkTruncation);
    };
  }, []);

  const classes = cn("min-w-0 max-w-full", className);

  return (
    <div ref={ref} className={classes}>
      {isTruncated ? (
        <TooltipTrigger placement="top" block>
          {children}
          <Tooltip>{title}</Tooltip>
        </TooltipTrigger>
      ) : (
        children
      )}
    </div>
  );
};
